import { C as Component } from "./component-CZdU8SYg.js";
import { V as VariantSelectedEvent } from "./events-C4hlH4zR.js";
import {
  d as defineCustomElement,
  u as updateGalleryMedia,
  f as formatMoney,
} from "./utilities-D68o91ht.js";
/* empty css                         */
class SpecialSaleSelector extends Component {
  /** @type {Object[]} All product variants */
  #variants = [];
  /** @type {number} Currently selected deal index */
  #selectedDeal = 2;
  /** @type {FormData|null} Wallet-only FormData for fee-failure fallback */
  #walletOnlyFormData = null;
  /** @type {boolean} True while a cart-add originated from this component is in flight */
  #loading = false;
  /** @type {HTMLInputElement[]} Checked extra inputs (FBT/upsell) to uncheck after successful add */
  #pendingExtrasToReset = [];
  connectedCallback() {
    super.connectedCallback();
    this.#variants = JSON.parse(this.dataset.variantsJson || "[]");
    this.selectDeal({ deal: this.#selectedDeal });
    this.querySelectorAll("[data-wallet]").forEach((wallet) => {
      this.#resolveWalletVariant(wallet);
    });
    this.#updateDealPrices();
    this.#syncFirstWallet();
    this.addEventListener("change", this.#onOptionChange);
    this.addEventListener("input", this.#onTextInput);
    this.addEventListener("click", this.#onClick);
    document.addEventListener("liquid-ajax-cart:request-end", this.#onCartRequestEnd, {
      signal: this.signal,
    });
    const productSection = this.closest('[data-section-type="product"]');
    if (productSection) {
      productSection.addEventListener("submit", this.#onFormSubmit, {
        capture: true,
        signal: this.signal,
      });
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.#onOptionChange);
    this.removeEventListener("input", this.#onTextInput);
  }
  /**
   * Select a deal card — called via on:click="/selectDeal?deal=N"
   * @param {{ deal: number }} data
   */
  selectDeal(data) {
    const dealId = typeof data === "object" ? data.deal : data;
    this.#selectedDeal = dealId;
    this.querySelectorAll("[data-deal]").forEach((card) => {
      const isActive = parseInt(card.dataset.deal) === dealId;
      card.classList.toggle("deal-active", isActive);
      const wallets = card.querySelector("[data-deal-wallets]");
      if (wallets) {
        wallets.classList.toggle("hidden", !isActive);
      }
      const iconPlus = card.querySelector("[data-icon-plus]");
      const iconCheck = card.querySelector("[data-icon-check]");
      if (iconPlus) {
        iconPlus.classList.toggle("hidden", isActive);
      }
      if (iconCheck) {
        iconCheck.classList.toggle("hidden", !isActive);
      }
    });
    this.#updateDealPrices();
    this.#syncFirstWallet();
  }
  /** Handle variant option and personalisation changes */
  #onOptionChange = (event) => {
    const target = event.target;
    if (target.hasAttribute("data-option-selector-wallet")) {
      const wallet = target.closest("[data-wallet]");
      if (wallet) {
        this.#resolveWalletVariant(wallet);
        this.#updateWalletLegend(wallet, target);
        this.#updateBestsellerBadge(wallet, target);
        this.#updateDealPrices();
        if (
          wallet.dataset.wallet === "0" &&
          wallet.closest("[data-deal]")?.classList.contains("deal-active")
        ) {
          this.#syncFirstWallet();
        }
      }
      return;
    }
    if (target.hasAttribute("data-personalisation-toggle-wallet")) {
      const wallet = target.closest("[data-wallet]");
      if (wallet) {
        const isOn = target.value === "mit";
        this.#togglePersonalisation(wallet, isOn);
        this.#toggleGridActive(isOn);
        this.#updateEngravingPreview(wallet);
        this.#updateDealPrices();
        this.#updatePagePrice();
        if (isOn && window.matchMedia("(max-width: 767px)").matches) {
          this.#scrollToMockup({ scrollPageIntoView: false });
          const engravingInput = wallet.querySelector("[data-personalisation-text-wallet]");
          if (engravingInput) {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                const rect = engravingInput.getBoundingClientRect();
                const targetTop = window.innerHeight * 0.25;
                window.scrollTo({ top: window.scrollY + rect.top - targetTop, behavior: "smooth" });
              })
            );
          }
        } else if (!isOn && window.matchMedia("(max-width: 767px)").matches) {
          const activeDeal = this.querySelector(`[data-deal="${this.#selectedDeal}"]`);
          const anyEngravingActive =
            activeDeal &&
            activeDeal.querySelectorAll('[data-personalisation-toggle-wallet][value="mit"]:checked')
              .length > 0;
          if (!anyEngravingActive) {
            this.#scrollToFirstSlide();
          }
        }
      }
    }
  };
  /** Handle "Vorschau anzeigen" button click (mobile only) — jumps the carousel into view. */
  #onClick = (event) => {
    const trigger = event.target.closest("[data-engraving-preview-trigger]");
    if (!trigger) {
      return;
    }
    this.#scrollToMockup({ scrollPageIntoView: true });
  };
  /** Handle text input for personalisation char counter */
  #onTextInput = (event) => {
    const target = event.target;
    if (target.hasAttribute("data-personalisation-text-wallet")) {
      const converted = target.value.replace(
        /❤️?|🧡|💛|💚|💙|💜|🖤|🤍|🤎|💗|💓|💞|💕|💖|💘|💝|💔|💟|♡/gu,
        "♥"
      );
      const sanitized = converted.replace(/[^A-Za-zÄÖÜäöüß0-9 .,\-_'&!?♥]/g, "");
      if (sanitized !== target.value) {
        const pos = target.selectionStart;
        target.value = sanitized;
        target.setSelectionRange(pos - 1, pos - 1);
      }
      const wallet = target.closest("[data-wallet]");
      if (wallet) {
        this.#updateCounter(wallet);
        this.#updateEngravingPreview(wallet);
      }
    }
  };
  /**
   * Intercept form submit in capture phase — before ajax-cart-product-form.
   * Prevents default, collects wallet items, and POSTs to /cart/add.js directly.
   */
  #onFormSubmit = (event) => {
    if (!event.target.matches('form[action*="/cart/add"]')) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#submitCart();
  };
  #submitCart() {
    const activeDeal = this.querySelector(`[data-deal="${this.#selectedDeal}"]`);
    if (!activeDeal) {
      return;
    }
    const walletEls = Array.from(activeDeal.querySelectorAll("[data-wallet]"));
    const isMultiWallet = walletEls.length > 1;
    for (const wallet of walletEls) {
      const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
      if (textInput && !textInput.disabled && !textInput.value.trim()) {
        this.#showEngravingValidationError(wallet, textInput);
        return;
      }
    }
    const items = [];
    for (const wallet of walletEls) {
      if (!wallet) {
        return;
      }
      const variantId = wallet.dataset.resolvedVariantId;
      if (!variantId) {
        return;
      }
      const item = { id: parseInt(variantId), quantity: 1 };
      const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
      if (textInput && !textInput.disabled && textInput.value.trim()) {
        const propertyName = textInput.dataset.propertyName || "Gravur";
        item.properties = { [propertyName]: textInput.value.trim() };
      }
      if (isMultiWallet) {
        item.properties = { ...item.properties, _slot: wallet.dataset.wallet };
      }
      items.push(item);
    }
    const feeVariantId = this.dataset.engravingFeeVariantId;
    const engravingCount = walletEls.filter((wallet) => {
      const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
      return textInput && !textInput.disabled && textInput.value.trim();
    }).length;
    const walletOnlyItems = items.map((item) => ({ id: item.id, quantity: item.quantity }));
    this.#walletOnlyFormData = engravingCount > 0 ? this.#buildFormData(walletOnlyItems) : null;
    const primaryItems =
      feeVariantId && engravingCount > 0
        ? [
            ...items,
            {
              id: parseInt(feeVariantId),
              quantity: engravingCount,
              properties: { _type: "engraving-fee" },
            },
          ]
        : items;
    const btExtras = Array.from(
      document.querySelectorAll(
        'bought-together-item[data-cart-bundle-item][data-variant-id] input[type="checkbox"]:checked'
      )
    )
      .map((input) => {
        const wrapper = input.closest("bought-together-item");
        if (!wrapper || wrapper.dataset.available !== "true") {
          return null;
        }
        const variantSelect = wrapper.querySelector("[data-bt-variant-select]");
        const variantId = variantSelect ? variantSelect.value : wrapper.dataset.variantId;
        const qtyInput = wrapper.querySelector("[data-bt-qty-input]");
        const qtyValue = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
        const quantity = Number.isFinite(qtyValue) && qtyValue > 0 ? qtyValue : 1;
        return variantId ? { input, id: parseInt(variantId), quantity } : null;
      })
      .filter(Boolean);
    const discountCodes = /* @__PURE__ */ new Set();
    const legacyExtras = Array.from(
      document.querySelectorAll(
        'product-upsell-toggle[data-variant-id] input[type="checkbox"]:checked'
      )
    )
      .map((input) => {
        const wrapper = input.closest("product-upsell-toggle");
        const variantId = wrapper?.dataset.variantId;
        if (wrapper?.dataset.discountCode) {
          discountCodes.add(wrapper.dataset.discountCode);
        }
        return variantId ? { input, id: parseInt(variantId), quantity: 1 } : null;
      })
      .filter(Boolean);
    const allExtras = [...btExtras, ...legacyExtras];
    this.#pendingExtrasToReset = allExtras.map((e) => e.input);
    const allItems = [...primaryItems, ...allExtras.map(({ id, quantity }) => ({ id, quantity }))];
    this.#setLoading(true);
    window.liquidAjaxCart.add(this.#buildFormData(allItems), {
      info: {
        source: "special-sale-selector",
        hasFee: feeVariantId && engravingCount > 0,
        discountCodes: discountCodes.size > 0 ? [...discountCodes] : null,
      },
    });
  }
  #setLoading(loading) {
    if (!loading && !this.#loading) {
      return;
    }
    this.#loading = loading;
    document.querySelectorAll("ajax-cart-product-form").forEach((form) => {
      const btn = form.querySelector(".product-buy-button");
      if (!btn) {
        return;
      }
      if (loading) {
        form.setAttribute("processing", "");
        btn.disabled = true;
      } else {
        form.removeAttribute("processing");
        btn.disabled = false;
      }
    });
  }
  /** Build Shopify /cart/add.js FormData from an items array */
  #buildFormData(items) {
    const formData = new FormData();
    items.forEach((item, i) => {
      formData.append(`items[${i}][id]`, String(item.id));
      formData.append(`items[${i}][quantity]`, String(item.quantity));
      if (item.properties) {
        for (const [key, value] of Object.entries(item.properties)) {
          formData.append(`items[${i}][properties][${key}]`, value);
        }
      }
    });
    return formData;
  }
  /**
   * Resolve selected options to a variant ID for a given wallet
   * @param {Element} wallet
   */
  #resolveWalletVariant(wallet) {
    const selectedOptions = {};
    wallet.querySelectorAll("[data-option-selector-wallet]:checked").forEach((input) => {
      const fieldset = input.closest("fieldset");
      const position = parseInt(fieldset.dataset.optionPosition);
      selectedOptions[position] = input.value;
    });
    const pickedPositions = Object.keys(selectedOptions);
    if (
      pickedPositions.length === 1 &&
      this.#variants.length > 0 &&
      this.#variants[0].options.length === 2
    ) {
      const mirrorValue = selectedOptions[pickedPositions[0]];
      if (mirrorValue) {
        for (let pos = 1; pos <= 2; pos++) {
          if (!selectedOptions[String(pos)]) {
            selectedOptions[String(pos)] = mirrorValue;
          }
        }
      }
    }
    const matched = this.#variants.find((variant) =>
      variant.options.every((opt, i) => selectedOptions[i + 1] === opt)
    );
    if (matched) {
      wallet.dataset.resolvedVariantId = matched.id;
      wallet.dataset.resolvedPrice = matched.price;
    }
  }
  /**
   * Sync the product page with the first wallet of the active deal.
   * Updates variant ID input, price, gallery, URL, and button state.
   */
  #syncFirstWallet() {
    const activeDeal = this.querySelector(`[data-deal="${this.#selectedDeal}"]`);
    const firstWallet = activeDeal?.querySelector('[data-wallet="0"]');
    if (!firstWallet?.dataset.resolvedVariantId) {
      return;
    }
    const variant = this.#variants.find(
      (v) => String(v.id) === firstWallet.dataset.resolvedVariantId
    );
    if (!variant) {
      return;
    }
    const productSection = this.closest('[data-section-type="product"]');
    if (!productSection) {
      return;
    }
    const variantInput = productSection.querySelector("#variant-id-input");
    if (variantInput) {
      variantInput.value = variant.id;
    }
    this.#updatePagePrice();
    const button = productSection.querySelector("#product-buy-buttons button[type='submit']");
    if (button) {
      button.disabled = !variant.available;
      const buttonText = button.querySelector(".button-text");
      if (buttonText) {
        buttonText.textContent = variant.available
          ? window.Theme?.translations?.products?.addToCart || "In den Warenkorb"
          : window.Theme?.translations?.products?.soldOut || "Ausverkauft";
      }
    }
    if (variant.id) {
      const productUrl = this.dataset.productUrl || window.location.pathname;
      const variantUrl = new URL(productUrl, window.location.origin);
      const variantParams = new URLSearchParams(window.location.search);
      variantParams.set("variant", variant.id);
      variantUrl.search = variantParams.toString();
      window.history.replaceState({}, "", variantUrl.toString());
    }
    const gallery = productSection.querySelector(".product-media-gallery");
    if (gallery) {
      gallery.dataset.variantId = variant.id;
      const positionsScript = gallery.querySelector("script[data-variant-positions]");
      if (positionsScript) {
        try {
          const positions = JSON.parse(positionsScript.textContent);
          const range = positions[variant.id];
          if (range) {
            this.#updateGallery(gallery, range.start, range.end);
          }
        } catch {}
      }
    }
    this.dataset.currentPrice = String(variant.price);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: String(variant.id),
        variantId: String(variant.id),
        price: variant.price,
      })
    );
  }
  /** Toggle gallery media visibility based on variant position range */
  #updateGallery(gallery, start, end) {
    updateGalleryMedia(gallery, start, end);
  }
  /** Update option legend text after selection */
  #updateWalletLegend(wallet, selectedElement) {
    const fieldset = selectedElement.closest("fieldset");
    const labelText = fieldset?.querySelector(".label-text");
    if (labelText) {
      const optionName = fieldset.dataset.legendLabel || fieldset.dataset.optionName;
      const value = selectedElement.dataset.value || selectedElement.value;
      labelText.textContent = `${optionName}: ${value}`;
    }
  }
  /**
   * Show/hide the Bestseller badge in the fieldset legend when the selected
   * color matches "Designer-Schwarz". The badge is rendered server-side on
   * Deal 2, wallet 0 (see special-sale.liquid); a no-op where no badge exists.
   * @param {Element} wallet
   * @param {HTMLInputElement} selectedElement
   */
  #updateBestsellerBadge(wallet, selectedElement) {
    const fieldset = selectedElement.closest("fieldset");
    const badge = fieldset?.querySelector("[data-bestseller-badge]");
    if (!badge) {
      return;
    }
    const value = (selectedElement.dataset.value || selectedElement.value || "").toLowerCase();
    const isDesignerSchwarz = value === "designer-schwarz" || value === "designer schwarz";
    badge.classList.toggle("is-visible", isDesignerSchwarz);
  }
  /** Toggle personalisation input visibility per wallet */
  #togglePersonalisation(wallet, isOn) {
    const inputWrapper = wallet.querySelector("[data-personalisation-input-wallet]");
    const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
    if (inputWrapper) {
      inputWrapper.classList.toggle("hidden", !isOn);
    }
    if (textInput) {
      textInput.disabled = !isOn;
      if (isOn) {
        textInput.focus({ preventScroll: true });
      } else {
        textInput.value = "";
        this.#updateCounter(wallet);
      }
    }
  }
  /** Update personalisation character counter */
  #updateCounter(wallet) {
    const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
    const counter = wallet.querySelector("[data-personalisation-counter-wallet]");
    if (textInput && counter) {
      const maxLen = textInput.maxLength || 30;
      counter.textContent = `${textInput.value.length}/${maxLen}`;
    }
  }
  /**
   * Scroll to the currently visible engraving mockup — grid-item on desktop,
   * carousel-slide on mobile. Picks the one that is actually laid out.
   * With scrollPageIntoView=true, also scrolls the page so the mockup is in viewport
   * (used by the mobile "Vorschau anzeigen" button).
   */
  #scrollToMockup({ scrollPageIntoView = false } = {}) {
    const mockups = document.querySelectorAll(".engraving-mockup-slide");
    const visible = Array.from(mockups).find((m) => m.getBoundingClientRect().width > 0);
    if (!visible) {
      return;
    }
    const carousel = visible.closest("carousel-embla");
    if (carousel?.embla) {
      const idx = carousel.embla.slideNodes().indexOf(visible);
      const snapCount = carousel.embla.snapList?.().length ?? 0;
      if (scrollPageIntoView) {
        carousel.embla.goTo(snapCount > 0 ? snapCount - 1 : idx);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const savedY = window.scrollY;
        carousel.embla.goTo(snapCount > 0 ? snapCount - 1 : idx);
        requestAnimationFrame(() => window.scrollTo({ top: savedY, behavior: "instant" }));
      }
    } else {
      visible.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
  /** Navigate the product carousel back to the first slide (called when engraving is turned off on mobile). */
  #scrollToFirstSlide() {
    const productSection = this.closest('[data-section-type="product"]');
    const carousel = productSection?.querySelector(".product-media-gallery carousel-embla");
    if (carousel?.embla) {
      const savedY = window.scrollY;
      carousel.embla.goTo(0);
      requestAnimationFrame(() => window.scrollTo({ top: savedY, behavior: "instant" }));
    }
  }
  /** Toggle the desktop grid into "engraving active" mode — moves the mockup into hero slot. */
  #toggleGridActive(isOn) {
    document.querySelectorAll(".product-media-grid").forEach((grid) => {
      grid.classList.toggle("engraving-active", isOn);
    });
  }
  /** Clear engraving preview + grid hero state when a cart-add succeeds; retry without fee on fee-add failure. */
  #onCartRequestEnd = (event) => {
    const { requestState } = event.detail ?? {};
    const type = requestState?.requestType;
    if (type !== "add") {
      return;
    }
    if (
      !requestState.responseData?.ok &&
      requestState.info?.source === "special-sale-selector" &&
      requestState.info?.hasFee &&
      this.#walletOnlyFormData
    ) {
      const fallback = this.#walletOnlyFormData;
      this.#walletOnlyFormData = null;
      window.liquidAjaxCart.add(fallback);
      this.#showEngravingFeeError();
      return;
    }
    if (!requestState.responseData?.ok) {
      this.#setLoading(false);
      this.#pendingExtrasToReset = [];
      return;
    }
    this.#setLoading(false);
    this.#walletOnlyFormData = null;
    this.#toggleGridActive(false);
    document.querySelectorAll("[data-engraving-preview]").forEach((p) => {
      p.textContent = "";
    });
    this.#pendingExtrasToReset.forEach((input) => {
      input.checked = false;
    });
    this.#pendingExtrasToReset = [];
    const discountCodes = requestState.info?.discountCodes;
    if (discountCodes?.length) {
      fetch(`/discount/${encodeURIComponent(discountCodes[0])}`, { credentials: "same-origin" })
        .then(() => window.liquidAjaxCart.get())
        .catch(() => {});
    }
  };
  /** Show inline validation error when engraving is selected but text input is empty. */
  #showEngravingValidationError(wallet, textInput) {
    const wrapper = wallet.querySelector("[data-personalisation-input-wallet]");
    if (!wrapper) {
      return;
    }
    let msg = wrapper.querySelector("[data-engraving-validation-error]");
    if (!msg) {
      msg = document.createElement("p");
      msg.setAttribute("data-engraving-validation-error", "");
      msg.className = "text-xs text-error mt-1";
      msg.textContent =
        window.Theme?.translations?.engravingRequired || "Bitte gib einen Gravurtext ein.";
      wrapper.appendChild(msg);
    }
    textInput.focus();
    textInput.addEventListener("input", () => msg.remove(), { once: true });
  }
  /** Show a temporary error notice when the engraving fee product is unavailable. */
  #showEngravingFeeError() {
    const existing = this.querySelector("[data-engraving-fee-error]");
    if (existing) {
      return;
    }
    const msg = document.createElement("p");
    msg.setAttribute("data-engraving-fee-error", "");
    msg.className = "text-sm text-error mt-2";
    msg.textContent =
      window.Theme?.translations?.engravingFeeUnavailable ||
      "Gravur-Aufpreis aktuell nicht verfügbar – Artikel ohne Gravur hinzugefügt.";
    this.prepend(msg);
    setTimeout(() => msg.remove(), 6e3);
  }
  /** Update the gallery overlay with the engraving text of the given wallet. */
  #updateEngravingPreview(wallet) {
    const textInput = wallet.querySelector("[data-personalisation-text-wallet]");
    const isOn =
      wallet.querySelector('[data-personalisation-toggle-wallet][value="mit"]')?.checked ?? false;
    const text = isOn && textInput && !textInput.disabled ? textInput.value : "";
    document.querySelectorAll("[data-engraving-preview]").forEach((p) => {
      p.textContent = text;
    });
  }
  /** Update deal card prices based on resolved wallet variants */
  #updateDealPrices() {
    const locale = window.Shopify?.locale || "de-DE";
    const currency = this.dataset.currency || "EUR";
    this.querySelectorAll("[data-deal]").forEach((card) => {
      const qty = parseInt(card.dataset.qty) || 1;
      const wallets = card.querySelectorAll("[data-wallet]");
      const firstWallet = wallets[0];
      if (!firstWallet?.dataset.resolvedPrice) {
        return;
      }
      const unitPrice = parseInt(firstWallet.dataset.resolvedPrice);
      const totalCompare = unitPrice * qty;
      const salePrice = unitPrice;
      const compareEl = card.querySelector("[data-deal-compare-price]");
      const saleEl = card.querySelector("[data-deal-sale-price]");
      if (this.dataset.upliftDesign) {
        if (compareEl) {
          compareEl.textContent = formatMoney(unitPrice * qty, { locale, currency });
          compareEl.classList.remove("hidden");
        }
        if (saleEl) {
          const amountEl = saleEl.querySelector("[data-unit-price-amount]");
          if (amountEl) {
            amountEl.textContent = formatMoney(Math.floor(unitPrice / 2), { locale, currency });
          } else {
            saleEl.textContent = `${formatMoney(Math.floor(unitPrice / 2), { locale, currency })}/Wallet`;
          }
        }
      } else {
        if (compareEl) {
          if (totalCompare > salePrice) {
            compareEl.textContent = formatMoney(totalCompare, { locale, currency });
            compareEl.classList.remove("hidden");
          } else {
            compareEl.classList.add("hidden");
          }
        }
        if (saleEl) {
          saleEl.textContent = formatMoney(salePrice, { locale, currency });
        }
      }
    });
  }
  /** Update main product page price + savings badge. Engraving fee never affects this price. */
  #updatePagePrice() {
    const locale = window.Shopify?.locale || "de-DE";
    const currency = this.dataset.currency || "EUR";
    const activeDeal = this.querySelector(`[data-deal="${this.#selectedDeal}"]`);
    const firstWallet = activeDeal?.querySelector('[data-wallet="0"]');
    if (!firstWallet?.dataset.resolvedVariantId) {
      return;
    }
    const variant = this.#variants.find(
      (v) => String(v.id) === firstWallet.dataset.resolvedVariantId
    );
    if (!variant) {
      return;
    }
    const effectivePrice = variant.price;
    const productSection = this.closest('[data-section-type="product"]');
    const priceContainer = productSection?.querySelector("#product-price");
    if (!priceContainer) {
      return;
    }
    const priceEl = priceContainer.querySelector(".font-bold:not(.line-through)");
    const compareEl = priceContainer.querySelector(".line-through");
    const badgeEl = priceContainer.querySelector(".badge");
    if (priceEl) {
      priceEl.textContent = formatMoney(effectivePrice, { locale, currency });
    }
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > effectivePrice) {
        compareEl.textContent = formatMoney(variant.compare_at_price, { locale, currency });
        compareEl.style.display = "";
      } else {
        compareEl.style.display = "none";
      }
    }
    if (
      badgeEl &&
      variant.compare_at_price > effectivePrice &&
      !badgeEl.dataset.freeGiftText &&
      !badgeEl.dataset.promoBadge
    ) {
      const savings = variant.compare_at_price - effectivePrice;
      const savingsPercent = Math.round((savings * 100) / variant.compare_at_price);
      const saveLabel = window.Theme?.translations?.products?.yourSave || "Du sparst";
      badgeEl.textContent = `${saveLabel}: ${formatMoney(savings, { locale, currency })} (${savingsPercent}%)`;
      badgeEl.style.display = "";
    }
  }
}
defineCustomElement("special-sale-selector", SpecialSaleSelector);
